import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * IntervalPicker — selector intuitiv pentru intervale IP (în zile).
 *
 * Props:
 *   value:    number | null — valoarea curentă în zile (null = nesetat)
 *   onChange: (days: number | null) => void
 *   label:    string — titlul câmpului (ex: "IP SSM")
 *   hint:     ReactNode — opțional, mesaj sub picker (ex: badge EPI)
 *   presets:  array — opțional, override la preset-urile default
 *   compact:  boolean — variantă mai mică pentru tabele/modale
 */

const DEFAULT_PRESETS = [
  { months: 1, days: 30 },
  { months: 3, days: 90 },
  { months: 6, days: 180 },
  { months: 12, days: 365 },
];

const IntervalPicker = ({
  value,
  onChange,
  label,
  hint = null,
  presets = DEFAULT_PRESETS,
  compact = false,
}) => {
  const { t } = useTranslation();

  const formatDays = (d) => {
    if (!d) return '';
    if (d % 365 === 0) return t('intervalPicker.years', { count: d / 365 });
    if (d % 30 === 0) return t('intervalPicker.months', { count: d / 30 });
    return t('intervalPicker.days', { count: d });
  };

  const presetDays = useMemo(() => presets.map((p) => p.days), [presets]);
  const isPreset = value && presetDays.includes(value);

  const [customMode, setCustomMode] = useState(!!value && !isPreset);
  const [customNum, setCustomNum] = useState(() => {
    if (!value || isPreset) return '';
    return value % 30 === 0 ? value / 30 : value;
  });
  const [customUnit, setCustomUnit] = useState(() => {
    if (!value || isPreset) return 'luni';
    return value % 30 === 0 ? 'luni' : 'zile';
  });

  // Dacă valoarea se schimbă din afară (ex: bulk apply), aliniem starea
  useEffect(() => {
    if (!value) {
      setCustomMode(false);
      setCustomNum('');
      return;
    }
    if (presetDays.includes(value)) {
      setCustomMode(false);
      return;
    }
    setCustomMode(true);
    setCustomNum(value % 30 === 0 ? value / 30 : value);
    setCustomUnit(value % 30 === 0 ? 'luni' : 'zile');
  }, [value, presetDays]);

  const applyCustom = (num, unit) => {
    if (!num || isNaN(parseInt(num))) {
      onChange(null);
      return;
    }
    const days = unit === 'luni' ? parseInt(num) * 30 : parseInt(num);
    onChange(days);
  };

  const sizeClass = compact ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1.5';

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
          {label}
        </label>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {presets.map((p) => {
          const active = !customMode && value === p.days;
          return (
            <button
              key={p.days}
              type="button"
              onClick={() => {
                setCustomMode(false);
                onChange(p.days);
              }}
              className={`${sizeClass} rounded-full border font-medium transition-colors ${
                active
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
              }`}
            >
              {p.label || t('intervalPicker.months', { count: p.months })}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => {
            setCustomMode(true);
            if (value && !presetDays.includes(value)) return;
            onChange(null);
            setCustomNum('');
          }}
          className={`${sizeClass} rounded-full border font-medium transition-colors ${
            customMode
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
          }`}
        >
          {t('intervalPicker.custom')}
        </button>
        {value !== null && value !== undefined && value !== '' && (
          <button
            type="button"
            onClick={() => {
              setCustomMode(false);
              setCustomNum('');
              onChange(null);
            }}
            className="text-xs text-slate-400 dark:text-slate-400 hover:text-danger-600 dark:hover:text-danger-300 underline ml-1"
          >
            {t('intervalPicker.clear')}
          </button>
        )}
      </div>

      {customMode && (
        <div className="flex items-center gap-2 mt-3">
          <input
            type="number"
            min="1"
            value={customNum}
            onChange={(e) => {
              setCustomNum(e.target.value);
              applyCustom(e.target.value, customUnit);
            }}
            placeholder={
              customUnit === 'luni'
                ? t('intervalPicker.exampleMonths')
                : t('intervalPicker.exampleDays')
            }
            className="input w-24 text-sm"
          />
          <select
            value={customUnit}
            onChange={(e) => {
              setCustomUnit(e.target.value);
              applyCustom(customNum, e.target.value);
            }}
            className="select w-24 text-sm"
          >
            <option value="luni">{t('intervalPicker.unitMonths')}</option>
            <option value="zile">{t('intervalPicker.unitDays')}</option>
          </select>
        </div>
      )}

      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {value ? (
          <>
            {t('intervalPicker.set')}{' '}
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {formatDays(value)}
            </span>{' '}
            {t('intervalPicker.daysParen', { days: value })}
          </>
        ) : (
          <span className="text-slate-400 dark:text-slate-400">{t('intervalPicker.notSet')}</span>
        )}
      </div>

      {hint && <div className="mt-2">{hint}</div>}
    </div>
  );
};

export default IntervalPicker;
